import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@/icons';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

/**
 * Reset password page component
 * Allows users to reset their password using a token from the email link
 */
export const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    const token = searchParams.get('token') || '';

    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordInputType, setPasswordInputType] = useState<'password' | 'text'>('password');
    const [confirmPasswordInputType, setConfirmPasswordInputType] = useState<'password' | 'text'>('password');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [isValidToken, setIsValidToken] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        newPassword?: string;
        confirmPassword?: string;
    }>({});
    const [error, setError] = useState<string | null>(null);

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setIsValidating(false);
                setIsValidToken(false);
                setError('Invalid reset link. Please request a new password reset.');
                return;
            }

            try {
                const response = await authApiService.validateResetToken(token);
                if (response.data.valid) {
                    setIsValidToken(true);
                } else {
                    // Token may be a signup token (user opened reset link but has invite link)
                    try {
                        const signupResponse = await authApiService.validateSignupToken(token);
                        if (signupResponse.data.success) {
                            navigate(`/auth/signup?token=${encodeURIComponent(token)}`, { replace: true });
                            return;
                        }
                    } catch {
                        // Not a valid signup token; show reset error below
                    }
                    setError(response.data.message || 'Invalid or expired reset token. Please request a new password reset.');
                }
            } catch (err) {
                debugError('Error validating reset token:', err);
                setError('An error occurred while validating the reset link. Please try again.');
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token]);

    // Set page title
    useEffect(() => {
        document.title = 'Reset Password | Calculist';
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        // Clear validation error for this field
        if (validationErrors[name as keyof typeof validationErrors]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: undefined,
            }));
        }
        setError(null);
    };

    const togglePasswordVisibility = (field: 'password' | 'confirm') => {
        if (field === 'password') {
            const newShowPassword = !showPassword;
            setShowPassword(newShowPassword);
            setPasswordInputType(newShowPassword ? 'text' : 'password');
        } else {
            const newShowConfirmPassword = !showConfirmPassword;
            setShowConfirmPassword(newShowConfirmPassword);
            setConfirmPasswordInputType(newShowConfirmPassword ? 'text' : 'password');
        }
    };

    const validateForm = (): boolean => {
        const errors: typeof validationErrors = {};

        if (!formData.newPassword) {
            errors.newPassword = 'Password is required';
        } else if (formData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long';
        }

        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await authApiService.resetPassword(token, formData.newPassword, formData.confirmPassword);
            setIsSuccess(true);
        } catch (err: any) {
            debugError('Password reset failed:', err);
            setError(err?.message || 'Failed to reset password. The link may have expired. Please request a new password reset.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isValidating) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: `4px solid ${theme.colors.border.default}`,
                        borderTop: `4px solid ${theme.colors.primary}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                    }}></div>
                    <p style={themedStyles.text.body}>Validating reset link...</p>
                </div>
            </div>
        );
    }

    if (!isValidToken || error) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                        <img
                            src="/logo-name.svg"
                            alt="Calculist"
                            style={{
                                height: '64px',
                                width: 'auto',
                                marginBottom: theme.spacing.md,
                            }}
                        />
                    </div>

                    {error && (
                        <div style={themedStyles.alert.error}>
                            <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                            <div>
                                <strong>Invalid Reset Link</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: theme.spacing.lg, textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/auth/login')}
                            style={themedStyles.button.primary}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                        <img
                            src="/logo-name.svg"
                            alt="Calculist"
                            style={{
                                height: '64px',
                                width: 'auto',
                                marginBottom: theme.spacing.md,
                            }}
                        />
                    </div>

                    <div style={themedStyles.alert.success}>
                        <span style={{ fontSize: '20px', flexShrink: 0 }}>✅</span>
                        <div>
                            <strong>Password Reset Successful</strong>
                            <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm }}>
                                Your password has been reset successfully. You can now log in with your new password.
                            </p>
                        </div>
                    </div>

                    <div style={{ marginTop: theme.spacing.lg, textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/auth/login')}
                            style={themedStyles.button.primary}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            <div style={themedStyles.card.default}>
                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img
                        src="/logo-name.svg"
                        alt="Calculist"
                        style={{
                            height: '64px',
                            width: 'auto',
                            marginBottom: theme.spacing.md,
                        }}
                    />
                    <p style={themedStyles.text.muted}>Reset Your Password</p>
                </div>

                {error && (
                    <div style={themedStyles.alert.error}>
                        <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                        <div>
                            <strong>Error</strong>
                            <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                {error}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setError(null)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                color: theme.colors.status.error,
                                cursor: 'pointer',
                                padding: '0',
                                marginLeft: 'auto',
                                flexShrink: 0,
                            }}
                            aria-label="Dismiss error"
                        >
                            ×
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xl }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <label htmlFor="newPassword" style={themedStyles.text.label}>
                            New Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={passwordInputType}
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                placeholder="Enter new password (min 8 characters)"
                                disabled={isSubmitting}
                                style={{
                                    ...themedStyles.input.default,
                                    ...(validationErrors.newPassword ? themedStyles.input.error : {}),
                                    paddingRight: '40px',
                                }}
                                autoComplete="new-password"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('password')}
                                disabled={isSubmitting}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.colors.text.secondary,
                                    fontSize: '16px',
                                    opacity: isSubmitting ? 0.5 : 1,
                                }}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                        </div>
                        {validationErrors.newPassword && (
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>
                                {validationErrors.newPassword}
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <label htmlFor="confirmPassword" style={themedStyles.text.label}>
                            Confirm Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={confirmPasswordInputType}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm new password"
                                disabled={isSubmitting}
                                style={{
                                    ...themedStyles.input.default,
                                    ...(validationErrors.confirmPassword ? themedStyles.input.error : {}),
                                    paddingRight: '40px',
                                }}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirm')}
                                disabled={isSubmitting}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.colors.text.secondary,
                                    fontSize: '16px',
                                    opacity: isSubmitting ? 0.5 : 1,
                                }}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                            </button>
                        </div>
                        {validationErrors.confirmPassword && (
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>
                                {validationErrors.confirmPassword}
                            </span>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                            ...themedStyles.button.primary,
                            ...(isSubmitting ? themedStyles.button.disabled : {}),
                            padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                            fontSize: theme.typography.fontSize.lg,
                            fontWeight: theme.typography.fontWeight.semibold,
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid transparent',
                                    borderTop: '2px solid currentColor',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                }}></div>
                                Resetting Password...
                            </>
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};
